import {
  GET_MANY,
  GET_MANY_REFERENCE,
  GET_LIST,
  GET_ONE,
  CREATE,
  UPDATE,
  UPDATE_MANY,
  DELETE,
  DELETE_MANY,
  fetchUtils,
} from "react-admin";

import debug from "debug";
import diff from "object-diff";
import { sendServerMessage } from "./lib/serverMessaging";

import { initializeClient, getClient } from "./serverClient";
import { getToken } from "./authProvider";

const dbg = debug("aor-feathers-client:rest-client");

function getIdKey({ resource, options }) {
  return (options[resource] && options[resource].id) || options.id || "id";
}

const doFind = (resource, service, query, token) => {
  if (!token) {
    console.log("No token, returning service");
    return service.find({
      query,
    });
  }

  return sendServerMessage({
    type: "FeathersGet",
    query,
    resource,
  }).then(detail => detail);
};

export default (apiUrl, options = {}) => {
  initializeClient(apiUrl);

  const client = getClient();

  const mapRequest = (type, resource, params) => {
    const usePatch = false;
    const idKey = getIdKey({ resource, options });
    dbg(
      "type=%o, resource=%o, params=%o, idKey=%o",
      type,
      resource,
      params,
      idKey
    );
    const service = client.service(resource);
    const query = {};

    const token = getToken();

    switch (type) {
      case GET_MANY: {
        const ids = params.ids || [];
        query[idKey] = { $in: ids };
        query.$limit = ids.length;
        return doFind(resource, service, query, token);
      }
      case GET_MANY_REFERENCE:
        if (params.target && params.id) {
          query[params.target] = params.id;
        }
      // eslint-disable-next-line
      case GET_LIST: {
        const { page, perPage } = params.pagination || {};
        const { field, order } = params.sort || {};
        dbg("field=%o, sort-key=%o", field);
        const sortVal = order === "DESC" ? -1 : 1;
        if (perPage && page) {
          query.$limit = perPage;
          query.$skip = perPage * (page - 1);
        }
        if (order) {
          const sortBy = field === "id" ? idKey : field;
          query.$sort = { [sortBy]: sortVal };
        }

        Object.assign(query, fetchUtils.flattenObject(params.filter));

        // HACK: special exception for kiosks filtering. Need a better
        // way to do this.
        if (resource === "kiosks" && "title" in params.filter) {
          query.title = {
            $ilike: `${params.filter.title}%`,
          };
        }

        dbg("query=%o", query);
        return doFind(resource, service, query, token);
      }
      case GET_ONE:
        return sendServerMessage({
          type: "FeathersOne",
          id: params.id,
          resource,
        }).then(detail => detail);
      case UPDATE:
        if (usePatch) {
          const data = params.previousData
            ? diff(params.previousData, params.data)
            : params.data;
          return service.patch(params.id, data, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
        }
        return service.update(params.id, params.data, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      case UPDATE_MANY:
        if (usePatch) {
          const data = params.previousData
            ? diff(params.previousData, params.data)
            : params.data;
          return Promise.all(params.ids.map(id => service.patch(id, data)));
        }
        return Promise.all(
          params.ids.map(id =>
            service.update(id, params.data, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            })
          )
        );
      case CREATE:
        return service.create(params.data, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      case DELETE:
        return service.remove(params.id, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      case DELETE_MANY:
        return Promise.all(
          params.ids.map(id =>
            service.remove(id, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            })
          )
        );
      default:
        throw new Error(
          `Unsupported FeathersJS restClient action type ${type}`
        );
    }
  };

  const mapResponse = (response, type, resource, params) => {
    const idKey = getIdKey({ resource, options });
    switch (type) {
      case GET_ONE:
      case UPDATE:
      case DELETE:
        return { data: { ...response, id: response[idKey] } };
      case UPDATE_MANY:
      case DELETE_MANY:
        return { data: response.map(record => record[idKey]) };
      case CREATE:
        return { data: { ...params.data, id: response[idKey] } };
      case GET_MANY_REFERENCE: // fix GET_MANY_REFERENCE missing id
      case GET_MANY: // fix GET_MANY missing id
      case GET_LIST: {
        if (!response) {
          console.warn("No response received");
          return null;
        }
        const data = response.map(item => {
          if (idKey !== "id") {
            // eslint-disable-next-line
            item.id = item[idKey];
          }
          return item;
        });
        const total = data.length;
        return {
          data,
          total,
        };
      }
      default:
        return response;
    }
  };

  // client
  // .authenticate()
  return (type, resource, params) =>
    mapRequest(type, resource, params).then(response =>
      mapResponse(response, type, resource, params)
    );
};



// WEBPACK FOOTER //
// ./src/dataProviderFeathers.js